import { XProvider } from "@ant-design/x";
import { XbotHistory, XbotPageContext } from "@dto/pyxbot-agent.dto";
import { Button, Modal, ModalProps, Popover, TooltipProps } from "antd";
import { createContext, FC, useEffect, useMemo, useState } from "react";
import { Label } from "src/data/Label";
import { Log } from "src/service/log.service";
import { updateUser } from "../userProfile/UserProfile";
import "./aiChat.less";
import { AIChatConversation } from "./AIChatConversation";
import { AIChatHistory } from "./AIChatHistory";
import { loadPyxbotHistory } from "./chatService";
import { signedInHistoryOwner } from "./historyOwner";
import AIChatPopover, { ChatIconButton } from "./components/AIChatPopover";
import { HELP_URL } from "./components/starterQuestions";
import { XbotTrialNote } from "./XbotTrialNote";

class ChatModel {
  conversation: XbotHistory | undefined;
  setConversation: React.Dispatch<React.SetStateAction<XbotHistory | undefined>>;
  history: XbotHistory[];
  setHistory: React.Dispatch<React.SetStateAction<XbotHistory[]>>;
  chatOpen: boolean;
  historyOpen: boolean;
  setHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** What the page the chat was opened on is about, when that page has an item to name. */
  pageContext?: XbotPageContext;
  /** Told after a link in an answer opened a page, so a large window can step aside (AIChatPopover). */
  onPageOpened?: () => void;
}

export const ChatContext = createContext(new ChatModel());

export const AIChat: FC<{ open: boolean; pageContext?: XbotPageContext; onPageOpened?: () => void }> = (props) => {
  const [conversation, setConversation] = useState<XbotHistory>();
  const [history, setHistory] = useState<XbotHistory[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const ctx = useMemo<ChatModel>(() => ({
    conversation, setConversation, history, setHistory, chatOpen: props.open, historyOpen, setHistoryOpen, pageContext: props.pageContext,
    onPageOpened: props.onPageOpened,
  }), [conversation, setConversation, history, setHistory, props.open, historyOpen, setHistoryOpen, props.pageContext, props.onPageOpened]);

  // read again at every opening: one read before the person was known found nothing, and the next answer kept would
  // have written that empty list over theirs
  useEffect(() => {
    if (props.open) {
      setHistory(loadPyxbotHistory(signedInHistoryOwner()));
    }
  }, [props.open]);

  // No theme prop: let XProvider inherit the app's ConfigProvider theme.
  return <XProvider>
    <div className="xm-chat">
      <ChatContext.Provider value={ctx}>
        <AIChatConversation />
        <AIChatHistory />
      </ChatContext.Provider>
      <div className="xm-chat-disclaimer"><XbotTrialNote open={props.open} />{Label.XORBOT} can make mistakes. Check important <Button type="link" onClick={() => setDisclaimerOpen(true)}>info.</Button></div>
      <ChatDisclaimer open={disclaimerOpen} onOk={() => setDisclaimerOpen(false)} onCancel={() => setDisclaimerOpen(false)} />
    </div>
  </XProvider>;
};

const ChatDisclaimer: FC<ModalProps> = (props) => {
  return <Modal {...props} title="Disclaimer" cancelButtonProps={{ style: { display: "none" } }}
    // half the screen on a wide one, nearly all of it on a phone
    width={{ xs: "92vw", md: "70vw", xl: "50vw" }}>
    <div>
      This chatbot answers questions about your monitored infrastructure from XorMon data. Answers may be incomplete, outdated, or incorrect.
      <br /><br />
      <div className="text-bold">By using this chatbot, you acknowledge and agree to the following:</div>
      <ul>
        <li>The content provided is for informational purposes only and should not be considered professional advice (legal, medical, financial, etc.).</li>
        <li>We do not guarantee the accuracy, reliability, or suitability of any responses provided by the chatbot.</li>
        <li>Any reliance on information provided by this chatbot is at your own risk.</li>
        <li>We are not responsible for any damages, losses, or consequences resulting from the use of this chatbot or the information it provides.</li>
      </ul>
      If you require specific professional advice, please consult a qualified expert.
    </div>
  </Modal>;
};

export const ChatIcon: FC<{ promo?: boolean }> = (props) => {
  const onInfoOpenChange: TooltipProps["onOpenChange"] = (visible) => {
    if (visible) {
      updateUser({ configuration: { aiShown: true } }).then(() => {
        Log.debug("User AI shown updated.");
      }, (reason) => Log.error("Failed to update user info!", reason));
    }
  };

  return props.promo ? <Popover
    title={Label.XORBOT}
    onOpenChange={onInfoOpenChange}
    trigger="click" placement="topRight"
    /* Only ever drawn while no LLM is set up (App.tsx), so it says what the chat would do and who can turn it on -
       never that it is there to be used. */
    content={<div>
      {Label.XORMON_NG} can answer questions about your infrastructure with {Label.XORBOT}.<br />
      It needs an administrator to set up an AI model in Settings &gt; AI.<br />
      Please refer to the <a target="_blank" rel="noopener noreferrer" href={HELP_URL}>documentation</a>
    </div>}>
    <ChatIconButton aria-label={`About ${Label.XORBOT}`} />
  </Popover>
    : <AIChatPopover />;
};
